import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BehaviorSubject, filter, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AppConfigService } from './config.service';
import { ChatComponent } from "./core/chat/chat.component";
import { NotificationComponent } from "./core/notification/notification.component";
import { SpinnerComponent } from "./core/spinner/spinner.component";
import { AuthService } from './services/auth/auth.service';
import { LanguageService } from './services/language.service';
import { LoaderService } from './services/loader.service';
import { SiteHeaderComponent } from './site/site-header/site-header.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SiteHeaderComponent, SpinnerComponent, CommonModule, NotificationComponent, ChatComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'ElfieShop';
  isLoading$ = new BehaviorSubject<boolean>(false);
  configLoaded$?: Observable<boolean>;
  private destroy$ = new Subject<void>();
  isSitePage = true;

  constructor(
    private readonly loaderService: LoaderService,
    private readonly changeDetector: ChangeDetectorRef,
    public readonly configService: AppConfigService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly languageService: LanguageService,
  ) {

    router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isSitePage = this.usesSiteLayout(this.router.routerState.snapshot.root);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.configService.init();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.handleLanguageQueryParameter();
      });

    this.handleLanguageQueryParameter();

    this.loaderService.loading$
      .pipe(
        takeUntil(this.destroy$),
        tap((loadingState) => {
          this.isLoading$.next(loadingState);
          this.changeDetector.detectChanges();
        })
      )
      .subscribe();

    // Handle OAuth callback
    const isOAuthCallback = this.authService.handleOAuthCallback();

    if (!isOAuthCallback) {
      this.authService.init();
    }
  }

  ngAfterViewInit() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        const tree = this.router.parseUrl(this.router.url);
        if (tree.fragment) {
          this.scrollToFragment(tree.fragment);
        }
      });
  }

  private handleLanguageQueryParameter(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    let langParam = urlTree.queryParams['lang'];
    let fragment = urlTree.fragment;

    // Check if lang parameter is in the fragment (e.g., /#home?lang=en, /video-generator/2?lang=es)
    if (!langParam && fragment && fragment.includes('?')) {
      const fragmentParts = fragment.split('?');
      fragment = fragmentParts[0];
      const fragmentParams = new URLSearchParams(fragmentParts[1]);
      langParam = fragmentParams.get('lang');
    }

    if (langParam) {
      const languageChanged = this.languageService.setLanguageWithoutReload(langParam);

      // Remove lang from query params
      const newQueryParams = { ...urlTree.queryParams };
      delete newQueryParams['lang'];

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: newQueryParams,
        fragment: fragment || undefined,
        queryParamsHandling: '',
        replaceUrl: true
      }).then(() => {
        // If language was changed, reload the page to apply translations
        if (languageChanged) {
          window.location.reload();
        }
      });
    }
  }

  private usesSiteLayout(route: ActivatedRouteSnapshot): boolean {
    for (let current: ActivatedRouteSnapshot | null = route; current; current = current.firstChild) {
      if (current.data['siteLayout']) {
        return true;
      }
    }
    return false;
  }

  private scrollToFragment(fragment: string) {
    const maxAttempts = 10;
    let attempts = 0;

    const interval = setInterval(() => {
      const element = document.getElementById(fragment);
      attempts++;

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);
  }
}
